import { useEffect, useMemo } from 'react'
import { QueryHookOptions } from '@apollo/client'
import { PatientHistoryType } from 'src/graphql.generated'
import { GetAdvancedPatientHistoryQuery, useGetAdvancedPatientHistoryQuery } from './advancedPatientHistory.generated'

export type PatientHistoryEntry = GetAdvancedPatientHistoryQuery['patientHistory'][number]

const NO_ENTRIES: PatientHistoryEntry[] = []

// Gated on `coachPatientAssociations`, which an Advanced-only care team never holds — a refusal is an
// ordinary empty list here, not an error.
export const useAdvancedPatientHistory = (uid: string): PatientHistoryEntry[] => {
  const { data } = useGetAdvancedPatientHistoryQuery({ variables: { uid } })

  return data?.patientHistory ?? []
}

export const useAdvancedPatientHistoryState = (
  uid: string,
  {
    pollInterval = 0,
    fetchPolicy,
  }: Pick<QueryHookOptions<GetAdvancedPatientHistoryQuery>, 'pollInterval' | 'fetchPolicy'> = {}
): { entries: PatientHistoryEntry[]; loading: boolean; failed: boolean } => {
  const { data, loading, error, startPolling, stopPolling } = useGetAdvancedPatientHistoryQuery({
    fetchPolicy,
    variables: { uid },
  })

  // Changing `pollInterval` through the query options would re-fetch immediately under cache-and-network.
  useEffect(() => {
    if (pollInterval > 0) {
      startPolling(pollInterval)
    }

    return stopPolling
  }, [pollInterval, startPolling, stopPolling])

  return { entries: data?.patientHistory ?? NO_ENTRIES, loading, failed: Boolean(error) }
}

export const useAdvancedPatientHistoryOfType = (
  uid: string,
  types: readonly PatientHistoryType[]
): PatientHistoryEntry[] => {
  const entries = useAdvancedPatientHistory(uid)

  return useMemo(() => entries.filter((entry) => entry.type && types.includes(entry.type)), [entries, types])
}
